import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { BOT_REGEX } from '@planone/bau365/util-restricted-access';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { WindowRef } from '@planone/shared/util-browser-api';
import { stringifyFilter } from '@planone/shared/util-interfaces';
import { debounceTime, distinctUntilChanged, of, withLatestFrom } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import * as DataAccessSearchActions from './data-access-search.actions';
import * as DataAccessSearchSelectors from './data-access-search.selectors';

import { SearchSortOrder, SearchType } from '../../model/search-filter';
import { DataAccessSearchService } from '../../service/data-access-search.service';
import * as DataAccessSearchOptionsActions from '../option-ids-visible/option-ids-visible.actions';
import { DateFilter } from '../option-ids-visible/search-filter-options.models';
import { OptionsFacade } from '../options-facade/options.facade';

@Injectable()
export class DataAccessSearchEffects {
  openSearchPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.searchPageOpened),
      map((action) => DataAccessSearchActions.loadSearchResults(action))
    )
  );

  loadSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.loadSearchResults),
      withLatestFrom(
        this.fairsFacade.fairId$,
        this.authenticationFacade.authenticated$,
        this.fairsFacade.isBauInsights$
      ),
      filter(
        ([_, __, authenticated, isBauInsights]) =>
          authenticated ||
          isBauInsights ||
          isPlatformServer(this.platformId) ||
          this.botRegex.test(this.windowRef.nativeWindow.navigator.userAgent)
      ),
      switchMap(([action, fairId]) =>
        this.searchService.getSearchResults(action.filters, fairId).pipe(
          map((response) => {
            return DataAccessSearchActions.searchResultsLoadSucceeded({
              ...response,
              requestedFilters: action.filters,
            });
          }),
          catchError((error) => of(DataAccessSearchActions.searchResultsLoadFailed({ error: error.status.toString() })))
        )
      )
    )
  );

  loadSearchCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.loadSearchResults),
      withLatestFrom(
        this.fairsFacade.fairId$,
        this.authenticationFacade.authenticated$,
        this.fairsFacade.isBauInsights$
      ),
      filter(
        ([_, __, authenticated, isBauInsights]) =>
          authenticated ||
          isBauInsights ||
          isPlatformServer(this.platformId) ||
          this.botRegex.test(this.windowRef.nativeWindow.navigator.userAgent)
      ),
      switchMap(([_, fairId]) =>
        this.searchService
          .getSearchResults(
            {
              searchTerm: '',
            },
            fairId,
            0
          )
          .pipe(
            map((response) => DataAccessSearchActions.searchCountsLoadSucceeded({ counts: response.counts })),
            catchError((error) =>
              of(DataAccessSearchActions.searchCountsLoadFailed({ error: error.status.toString() }))
            )
          )
      )
    )
  );

  searchFilterUnselected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DataAccessSearchOptionsActions.optionIdsVisibleValuesUnselected,
        DataAccessSearchOptionsActions.optionIdsVisibleValueUnselected,
        DataAccessSearchOptionsActions.searchFilterOptionValueChipRemoved
      ),
      withLatestFrom(this.store.pipe(select(DataAccessSearchSelectors.getSearchFilters)), this.fairsFacade.fairId$),
      switchMap(([action, filters, fairId]) => {
        const updatedFilters = { ...filters };
        updatedFilters.searchTerm = '';
        if (updatedFilters.stringFilter) {
          updatedFilters.stringFilter = updatedFilters.stringFilter.filter((fil) => {
            return fil.name !== action.searchFilterOption.key;
          });
        }

        return this.searchService.getSearchResults(updatedFilters, fairId).pipe(
          map((response) => {
            return DataAccessSearchActions.searchFiltersLoadAfterUncheckSucceeded({
              ...response,
              requestedFilters: filters,
            });
          }),
          catchError((error) =>
            of(DataAccessSearchActions.searchFiltersLoadAfterUncheckFailed({ error: error.status.toString() }))
          )
        );
      })
    )
  );

  detectEmptySearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.searchResultsLoadSucceeded),
      filter((action) => action.counts.all === 0),
      map((action) => DataAccessSearchActions.loadSearchTermSuggestions({ filters: action.requestedFilters }))
    )
  );

  loadInitialSearchFilterOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.searchResultsLoadSucceeded),
      withLatestFrom(this.fairsFacade.fairId$),
      distinctUntilChanged(([prevAction, prevFairId], [currentAction, currentFairId]) => {
        return !!(
          prevFairId === currentFairId &&
          prevAction.requestedFilters.searchTypes?.length === currentAction.requestedFilters.searchTypes?.length &&
          prevAction.requestedFilters.searchTypes?.every((value) =>
            currentAction.requestedFilters.searchTypes?.includes(value)
          )
        );
      }),
      switchMap(([action, fairId]) => {
        if (
          action.requestedFilters.stringFilter ||
          action.requestedFilters.searchTerm ||
          action.requestedFilters.dateFilter
        ) {
          return this.searchService
            .getSearchResults(
              {
                searchTypes: action.requestedFilters.searchTypes,
                searchSort: action.requestedFilters.searchSort,
                searchTerm: '',
              },
              fairId
            )
            .pipe(map((response) => response.filterOptions));
        } else {
          return of(action.filterOptions);
        }
      }),
      map((filterOptions) => DataAccessSearchOptionsActions.initialOptionIdsVisibleLoadSucceeded({ filterOptions })),
      catchError((error) =>
        of(DataAccessSearchOptionsActions.initialOptionIdsVisibleLoadFailed(error.status.toString()))
      )
    )
  );

  searchTypeSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessSearchActions.searchTypeSelected),
        withLatestFrom(this.store.select(DataAccessSearchSelectors.activeSearchTerm)),
        tap(([action, searchTerm]) => {
          let queryParams: {
            searchTypes: SearchType[] | null;
            page: number | null;
            filter?: any | null;
            dateFilter?: any | null;
            searchTerm: string | null;
          } = {
            searchTypes: null,
            page: null,
            searchTerm,
          };
          if (action.searchTypes.length > 1 || action.searchTypes[0] !== SearchType.all) {
            queryParams = {
              searchTypes: action.searchTypes,
              page: null,
              filter: null,
              dateFilter: null,
              searchTerm,
            };
          }
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  searchTermSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessSearchActions.searchTermSelected),
        tap((action) => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              searchTerm: action.searchTerm !== '' ? action.searchTerm : null,
              page: null,
              searchTypes: null,
              sort: null,
              filter: null,
              dateFilter: null,
            },
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  searchTermAutocompleteSubmit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataAccessSearchActions.searchFormValueChanged),
      withLatestFrom(this.fairsFacade.fairId$),
      debounceTime(200),
      switchMap(([action, fairId]) => {
        const searchTerm = action.searchTermFragment?.trim() ?? '';
        if (searchTerm !== '' && searchTerm.length >= 2) {
          return this.searchService.getAutocompleteSuggestion(searchTerm, fairId).pipe(
            map((response) =>
              DataAccessSearchActions.searchTermAutocompleteLoadSucceeded({
                suggestions: response.suggestions,
              })
            ),
            catchError((error) =>
              of(DataAccessSearchActions.searchTermAutocompleteLoadFailed({ error: error.status.toString() }))
            )
          );
        } else {
          return of(DataAccessSearchActions.searchFormValueIsInvalid());
        }
      })
    );
  });

  searchSortingSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessSearchActions.searchSortingSelected),
        withLatestFrom(this.store.select(DataAccessSearchSelectors.activeSearchTerm)),
        tap(([action, searchTerm]) => {
          const queryParams: {
            sort: SearchSortOrder | null;
            searchTerm: string | null;
          } = {
            searchTerm,
            sort: action.searchSortOrder === SearchSortOrder.recommended ? null : action.searchSortOrder,
          };
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  clearFilters = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessSearchOptionsActions.clearAllFilters),
        tap(() => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              searchTypes: null,
              filter: null,
              dateFilter: null,
              page: null,
            },
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  searchInstantFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DataAccessSearchOptionsActions.optionIdsVisibleValueSelected,
          DataAccessSearchOptionsActions.optionIdsVisibleValueUnselected,
          DataAccessSearchOptionsActions.optionIdsVisibleValuesSelected,
          DataAccessSearchOptionsActions.optionIdsVisibleValuesUnselected,
          DataAccessSearchOptionsActions.optionIdsVisibleUnselected,
          DataAccessSearchOptionsActions.searchFilterOptionValueChipRemoved,
          DataAccessSearchOptionsActions.unselectDateFilter,
          DataAccessSearchOptionsActions.selectedDatetimeFilterStart,
          DataAccessSearchOptionsActions.selectedDatetimeFilterEnd,
          DataAccessSearchOptionsActions.selectedDatetimeFilterReset,
          DataAccessSearchActions.searchFilterModalClosed
        ),
        withLatestFrom(
          this.searchFilterOptionsFacade.selectedOptionIdsVisible$,
          this.searchFilterOptionsFacade.selectedEventDatetimeFilter$,
          this.store.select(DataAccessSearchSelectors.activeSearchTerm),
          this.screensizeFacade.size$
        ),
        tap(([_action, stringFilters, dateFilter, searchTerm]) => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              filter: stringifyFilter(stringFilters),
              dateFilter: this.stringifyDateFilter(dateFilter),
              searchTerm,
              page: null,
            },
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  removeExhibitorSearchFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessSearchOptionsActions.removeExhibitorFilter),
        withLatestFrom(this.searchFilterOptionsFacade.selectedOptionIdsVisible$),
        tap(([_action, stringFilters]) => {
          const updatedStringFilters: { [key: string]: string[] } = {};
          for (const key in stringFilters) {
            if (key !== 'exhibitor') {
              updatedStringFilters[key] = stringFilters[key];
            }
          }
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              filter: stringifyFilter(updatedStringFilters),
              page: null,
            },
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  searchPaginationClicked$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessSearchActions.searchPaginationClicked),
        tap((action) => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { page: action.searchPageIndex > 1 ? action.searchPageIndex : null },
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  generateWatchlistDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.generateSearchDocument),
      withLatestFrom(this.store.select(DataAccessSearchSelectors.getSearchFilters), this.fairsFacade.fairId$),
      switchMap(([_, filters, fairId]) =>
        this.searchService.generateSearchDocument(fairId, filters).pipe(
          map((result) =>
            DataAccessSearchActions.generateSearchDocumentSuccess({
              documentId: result,
            })
          ),
          catchError(() => of(DataAccessSearchActions.generateSearchDocumentFailure()))
        )
      )
    )
  );

  loadSearchTermSuggestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.loadSearchTermSuggestions),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.searchService.getSearchTermSuggestions(action.filters, fairId).pipe(
          map(({ suggestions }) =>
            DataAccessSearchActions.searchTermSuggestionsLoadSucceeded({
              suggestions,
            })
          ),
          catchError((error) =>
            of(DataAccessSearchActions.searchTermSuggestionsLoadFailed({ error: error.status.toString() }))
          )
        )
      )
    )
  );

  loadResultsFromSuggestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.searchTermSuggestionsLoadSucceeded),
      filter((action) => action.suggestions.length > 0),
      map((action) => action.suggestions[0]),
      tap((suggestedSearchTerm) =>
        this.store.dispatch(DataAccessSearchActions.useSuggestedSearchTerm({ suggestedSearchTerm }))
      ),
      withLatestFrom(this.store.pipe(select(DataAccessSearchSelectors.getSearchFilters))),
      map(([suggestion, searchFilters]) =>
        DataAccessSearchActions.loadSearchResults({
          filters: {
            searchTerm: suggestion,
            searchTypes: searchFilters.searchTypes,
            searchSort: searchFilters.searchSort,
            stringFilter: searchFilters.stringFilter,
          },
        })
      )
    )
  );

  detectEmptySearchTermSuggestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.searchTermSuggestionsLoadSucceeded),
      filter((action) => action.suggestions.length === 0),
      map(() => DataAccessSearchActions.loadSearchResultsSuggestions())
    )
  );

  loadSearchResultsSuggestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessSearchActions.loadSearchResultsSuggestions),
      withLatestFrom(this.fairsFacade.fairId$, this.store.pipe(select(DataAccessSearchSelectors.getSearchFilters))),
      switchMap(([_action, fairId, searchFilters]) => {
        searchFilters.searchTerm = '';
        return this.searchService.getSearchResults(searchFilters, fairId, 3).pipe(
          map((response) => {
            return DataAccessSearchActions.searchResultSuggestionsLoadSucceeded({
              resultSuggestions: response.results,
            });
          }),
          catchError((error) =>
            of(DataAccessSearchActions.searchResultSuggestionsLoadFailed({ error: error.status.toString() }))
          )
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private searchService: DataAccessSearchService,
    private router: Router,
    private route: ActivatedRoute,
    private searchFilterOptionsFacade: OptionsFacade,
    private fairsFacade: DataAccessFairsFacade,
    private authenticationFacade: AuthenticationFacade,
    private toggleFacade: DataAccessTogglesFacade,
    private screensizeFacade: DataAccessScreensizeFacade,
    private store: Store,
    private windowRef: WindowRef,
    @Inject(BOT_REGEX) private botRegex: RegExp,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  private stringifyDateFilter(dateFilter: DateFilter): string | null {
    const { lower, upper } = dateFilter;
    if (!lower || !upper) return null;
    return JSON.stringify(dateFilter);
  }
}
